import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { ChevronDownIcon, ChevronUpIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { CalendarDaysIcon, ChartBarIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

function Production() {
  document.title = 'Production Logs';

  const { user } = useAuth();
  const [productionLogs, setProductionLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shops, setShops] = useState([]);
  const [machines, setMachines] = useState([]);

  // Filters
  const [filterShop, setFilterShop] = useState('');
  const [showShopDropdown, setShowShopDropdown] = useState(false);
  const [filterMachine, setFilterMachine] = useState('');
  const [showMachineDropdown, setShowMachineDropdown] = useState(false);
  const [dateFilter, setDateFilter] = useState('last30days');
  const [showDateDropdown, setShowDateDropdown] = useState(false);

  // View mode: 'table', 'chart', 'calendar'
  const [viewMode, setViewMode] = useState('table');

  // Sorting
  const [sortField, setSortField] = useState('production_date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Calendar state
  const [calendarDate, setCalendarDate] = useState(new Date());

  const dateFilterOptions = [
    { value: 'today', label: 'Today' },
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'thismonth', label: 'This Month' },
    { value: 'last3months', label: 'Last 3 Months' },
    { value: 'all', label: 'All Time' },
  ];

  const formatDateToString = (date) => {
    return date.toISOString().split('T')[0];
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      setLoading(true);
      try {
        const [logsResult, shopsResult, machinesResult] = await Promise.all([
          supabase
            .from('production_logs')
            .select(`
              log_id, job_id, part_number, quantity_produced, production_date, shop_id, machine_id,
              jobs(po_number, po_line_number),
              shops(id, name),
              machines(id, name)
            `)
            .order('production_date', { ascending: false }),
          supabase.from('shops').select('id, name'),
          supabase.from('machines').select('id, name, shop_id'),
        ]);

        if (logsResult.error) throw new Error(`Production logs fetch error: ${logsResult.error.message}`);
        if (shopsResult.error) throw new Error(`Shops fetch error: ${shopsResult.error.message}`);
        if (machinesResult.error) throw new Error(`Machines fetch error: ${machinesResult.error.message}`);

        const logs = Array.isArray(logsResult.data) ? logsResult.data : [];
        setProductionLogs(logs);
        setShops(shopsResult.data || []);
        setMachines(machinesResult.data || []);
      } catch (err) {
        console.error('Error fetching data:', err.message);
        toast.error(`Failed to fetch data: ${err.message}`);
        setProductionLogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Apply filters whenever filter state changes
  useEffect(() => {
    let filtered = [...productionLogs];

    // Date filter
    const now = new Date();
    const todayStr = formatDateToString(now);

    if (dateFilter === 'today') {
      filtered = filtered.filter(log => formatDateToString(new Date(log.production_date)) === todayStr);
    } else if (dateFilter === 'last7days') {
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(now.getDate() - 7);
      const sevenDaysAgoStr = formatDateToString(sevenDaysAgo);
      filtered = filtered.filter(log => {
        const logDateStr = formatDateToString(new Date(log.production_date));
        return logDateStr >= sevenDaysAgoStr && logDateStr <= todayStr;
      });
    } else if (dateFilter === 'last30days') {
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(now.getDate() - 30);
      const thirtyDaysAgoStr = formatDateToString(thirtyDaysAgo);
      filtered = filtered.filter(log => {
        const logDateStr = formatDateToString(new Date(log.production_date));
        return logDateStr >= thirtyDaysAgoStr && logDateStr <= todayStr;
      });
    } else if (dateFilter === 'thismonth') {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfMonthStr = formatDateToString(startOfMonth);
      filtered = filtered.filter(log => {
        const logDateStr = formatDateToString(new Date(log.production_date));
        return logDateStr >= startOfMonthStr && logDateStr <= todayStr;
      });
    } else if (dateFilter === 'last3months') {
      const threeMonthsAgo = new Date(now);
      threeMonthsAgo.setMonth(now.getMonth() - 3);
      const threeMonthsAgoStr = formatDateToString(threeMonthsAgo);
      filtered = filtered.filter(log => {
        const logDateStr = formatDateToString(new Date(log.production_date));
        return logDateStr >= threeMonthsAgoStr && logDateStr <= todayStr;
      });
    }

    // Shop filter
    if (filterShop) {
      filtered = filtered.filter(log => log.shops?.name === filterShop);
    }

    // Machine filter
    if (filterMachine) {
      filtered = filtered.filter(log => log.machines?.name === filterMachine);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';

      if (sortField === 'production_date') {
        const aDate = aValue ? new Date(aValue) : new Date(0);
        const bDate = bValue ? new Date(bValue) : new Date(0);
        return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }
      if (sortField === 'quantity_produced') {
        return sortDirection === 'asc' ? (aValue || 0) - (bValue || 0) : (bValue || 0) - (aValue || 0);
      }
      if (sortField === 'po_number') {
        aValue = a.jobs?.po_number || '';
        bValue = b.jobs?.po_number || '';
      }
      if (sortField === 'shop') {
        aValue = a.shops?.name || '';
        bValue = b.shops?.name || '';
      }
      if (sortField === 'machine') {
        aValue = a.machines?.name || '';
        bValue = b.machines?.name || '';
      }

      return sortDirection === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });

    setFilteredLogs(filtered);
  }, [productionLogs, dateFilter, filterShop, filterMachine, sortField, sortDirection]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const totalQuantity = filteredLogs.reduce((sum, log) => sum + (log.quantity_produced || 0), 0);
    const uniqueDays = new Set(filteredLogs.map(log => formatDateToString(new Date(log.production_date)))).size;
    const avgDaily = uniqueDays > 0 ? Math.round(totalQuantity / uniqueDays) : 0;

    const productionByShop = filteredLogs.reduce((acc, log) => {
      const shopName = log.shops?.name || 'Unknown';
      acc[shopName] = (acc[shopName] || 0) + (log.quantity_produced || 0);
      return acc;
    }, {});

    return {
      totalLogs: filteredLogs.length,
      totalQuantity,
      avgDaily,
      uniqueDays,
      productionByShop,
    };
  }, [filteredLogs]);

  // Chart data - daily production
  const chartData = useMemo(() => {
    const dailyProduction = {};

    filteredLogs.forEach(log => {
      const dateStr = formatDateToString(new Date(log.production_date));
      if (!dailyProduction[dateStr]) {
        dailyProduction[dateStr] = { date: dateStr, quantity: 0, NORTH: 0, SOUTH: 0 };
      }
      dailyProduction[dateStr].quantity += log.quantity_produced || 0;
      const shop = log.shops?.name || 'Unknown';
      if (shop === 'NORTH' || shop === 'SOUTH') {
        dailyProduction[dateStr][shop] += log.quantity_produced || 0;
      }
    });

    return Object.values(dailyProduction)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(d => ({
        ...d,
        displayDate: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }),
      }));
  }, [filteredLogs]);

  // Calendar data
  const calendarData = useMemo(() => {
    const dailyProduction = {};
    productionLogs.forEach(log => {
      const dateStr = formatDateToString(new Date(log.production_date));
      dailyProduction[dateStr] = (dailyProduction[dateStr] || 0) + (log.quantity_produced || 0);
    });
    return dailyProduction;
  }, [productionLogs]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleDeleteProductionLog = async (logId) => {
    if (!window.confirm('Are you sure you want to delete this production log?')) return;

    try {
      const { error } = await supabase.from('production_logs').delete().eq('log_id', logId);
      if (error) throw error;

      setProductionLogs(prev => prev.filter(log => log.log_id !== logId));
      toast.success('Production log deleted');
    } catch (err) {
      console.error('Error deleting production log:', err);
      toast.error(`Failed to delete: ${err.message}`);
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const renderCalendar = () => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);

    const days = [];
    const maxProduction = Math.max(...Object.values(calendarData), 1);

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-16" />);
    }

    // Days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const production = calendarData[dateStr] || 0;
      const intensity = production > 0 ? Math.min(0.9, (production / maxProduction) * 0.9 + 0.1) : 0;

      days.push(
        <div
          key={day}
          className="h-16 border border-slate-100 rounded-sm p-1 relative"
          style={production > 0 ? { backgroundColor: `rgba(59, 130, 246, ${intensity})` } : {}}
        >
          <span className={`text-xs font-medium ${production > 0 && intensity > 0.5 ? 'text-white' : 'text-slate-600'}`}>
            {day}
          </span>
          {production > 0 && (
            <div className={`absolute bottom-1 left-1 right-1 text-xs font-semibold ${intensity > 0.5 ? 'text-white' : 'text-blue-700'}`}>
              {production.toLocaleString()}
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  const filteredMachines = filterShop
    ? machines.filter(m => {
        const shop = shops.find(s => s.name === filterShop);
        return shop && m.shop_id === shop.id;
      })
    : machines;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 h-12 px-4 flex items-center">
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold text-slate-800">Production</h1>
            <span className="text-xs text-slate-500">{filteredLogs.length} log{filteredLogs.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <div className="flex border border-slate-200 rounded-sm overflow-hidden">
              <button
                onClick={() => setViewMode('chart')}
                className={`p-1.5 ${viewMode === 'chart' ? 'bg-slate-700 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                title="Chart View"
              >
                <ChartBarIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`p-1.5 ${viewMode === 'calendar' ? 'bg-slate-700 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                title="Calendar View"
              >
                <CalendarDaysIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-1.5 ${viewMode === 'table' ? 'bg-slate-700 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                title="Table View"
              >
                <TableCellsIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Date Filter */}
            <div className="relative">
              <button
                onClick={() => setShowDateDropdown(!showDateDropdown)}
                onBlur={() => setTimeout(() => setShowDateDropdown(false), 150)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors bg-white min-w-[130px] justify-between"
              >
                <span className="text-slate-800">
                  {dateFilterOptions.find(o => o.value === dateFilter)?.label}
                </span>
                <ChevronDownIcon className="w-4 h-4 text-slate-400" />
              </button>
              {showDateDropdown && (
                <div className="absolute z-50 top-full right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg min-w-[140px]">
                  {dateFilterOptions.map(option => (
                    <div
                      key={option.value}
                      onClick={() => { setDateFilter(option.value); setShowDateDropdown(false); }}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${dateFilter === option.value ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Shop Filter */}
            <div className="relative">
              <button
                onClick={() => setShowShopDropdown(!showShopDropdown)}
                onBlur={() => setTimeout(() => setShowShopDropdown(false), 150)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors bg-white min-w-[110px] justify-between"
              >
                <span className={filterShop ? 'text-slate-800' : 'text-slate-500'}>
                  {filterShop || 'All Shops'}
                </span>
                <ChevronDownIcon className="w-4 h-4 text-slate-400" />
              </button>
              {showShopDropdown && (
                <div className="absolute z-50 top-full right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg min-w-[120px]">
                  <div
                    onClick={() => { setFilterShop(''); setFilterMachine(''); setShowShopDropdown(false); }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${!filterShop ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                  >
                    All Shops
                  </div>
                  {shops.map(shop => (
                    <div
                      key={shop.id}
                      onClick={() => { setFilterShop(shop.name); setFilterMachine(''); setShowShopDropdown(false); }}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${filterShop === shop.name ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                    >
                      {shop.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Machine Filter */}
            <div className="relative">
              <button
                onClick={() => setShowMachineDropdown(!showMachineDropdown)}
                onBlur={() => setTimeout(() => setShowMachineDropdown(false), 150)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors bg-white min-w-[130px] justify-between"
              >
                <span className={filterMachine ? 'text-slate-800' : 'text-slate-500'}>
                  {filterMachine || 'All Machines'}
                </span>
                <ChevronDownIcon className="w-4 h-4 text-slate-400" />
              </button>
              {showMachineDropdown && (
                <div className="absolute z-50 top-full right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg min-w-[140px] max-h-64 overflow-y-auto">
                  <div
                    onClick={() => { setFilterMachine(''); setShowMachineDropdown(false); }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${!filterMachine ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                  >
                    All Machines
                  </div>
                  {filteredMachines.map(machine => (
                    <div
                      key={machine.id}
                      onClick={() => { setFilterMachine(machine.name); setShowMachineDropdown(false); }}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${filterMachine === machine.name ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                    >
                      {machine.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="px-4 py-3 bg-white border-b border-slate-200">
        <div className="flex items-center gap-6">
          <div>
            <span className="text-xs text-slate-500 uppercase tracking-wide">Total Produced</span>
            <p className="text-xl font-semibold text-slate-800 tabular-nums">{summaryStats.totalQuantity.toLocaleString()}</p>
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div>
            <span className="text-xs text-slate-500 uppercase tracking-wide">Avg Daily</span>
            <p className="text-xl font-semibold text-slate-800 tabular-nums">{summaryStats.avgDaily.toLocaleString()}</p>
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div>
            <span className="text-xs text-slate-500 uppercase tracking-wide">Active Days</span>
            <p className="text-xl font-semibold text-slate-800 tabular-nums">{summaryStats.uniqueDays}</p>
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div className="flex items-center gap-4">
            {Object.entries(summaryStats.productionByShop).map(([shop, qty]) => (
              <div key={shop} className="flex items-center gap-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium ${
                  shop === 'NORTH' ? 'bg-blue-100 text-blue-700' : shop === 'SOUTH' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                }`}>
                  {shop}
                </span>
                <span className="text-sm font-medium text-slate-700 tabular-nums">{qty.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Chart View */}
      {viewMode === 'chart' && (
        <div className="p-4">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Daily Production</h3>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="displayDate"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                    tickFormatter={(value) => value.toLocaleString()}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                    formatter={(value) => [value.toLocaleString(), 'Quantity']}
                  />
                  <Bar dataKey="NORTH" stackId="a" fill="#3b82f6" name="NORTH" />
                  <Bar dataKey="SOUTH" stackId="a" fill="#22c55e" name="SOUTH" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400">
                No production data for selected period
              </div>
            )}
          </div>
        </div>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="p-4">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-700">
                {calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h3>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1))}
                  className="p-1 hover:bg-slate-100 rounded-sm"
                >
                  <ChevronLeftIcon className="w-5 h-5 text-slate-600" />
                </button>
                <button
                  onClick={() => setCalendarDate(new Date())}
                  className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded-sm"
                >
                  Today
                </button>
                <button
                  onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1))}
                  className="p-1 hover:bg-slate-100 rounded-sm"
                >
                  <ChevronRightIcon className="w-5 h-5 text-slate-600" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs font-medium text-slate-500 py-1">
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {renderCalendar()}
            </div>
            <div className="mt-4 flex items-center justify-end gap-2 text-xs text-slate-500">
              <span>Less</span>
              <div className="flex gap-0.5">
                <div className="w-4 h-4 bg-slate-100 rounded-sm" />
                <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)' }} />
                <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: 'rgba(59, 130, 246, 0.4)' }} />
                <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: 'rgba(59, 130, 246, 0.6)' }} />
                <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: 'rgba(59, 130, 246, 0.8)' }} />
              </div>
              <span>More</span>
            </div>
          </div>
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="bg-white border-b border-slate-200">
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-50 border-b border-slate-200">
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('production_date')}
                >
                  <div className="flex items-center gap-1">
                    Date {sortField === 'production_date' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}
                  </div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('po_number')}
                >
                  <div className="flex items-center gap-1">
                    PO # {sortField === 'po_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Ln</th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('part_number')}
                >
                  <div className="flex items-center gap-1">
                    Part {sortField === 'part_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}
                  </div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('quantity_produced')}
                >
                  <div className="flex items-center gap-1">
                    Qty {sortField === 'quantity_produced' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}
                  </div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('shop')}
                >
                  <div className="flex items-center gap-1">
                    Shop {sortField === 'shop' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}
                  </div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('machine')}
                >
                  <div className="flex items-center gap-1">
                    Machine {sortField === 'machine' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredLogs.length > 0 ? (
                filteredLogs.map((log) => (
                  <tr key={log.log_id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700">
                      {new Date(log.production_date).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      {log.jobs?.po_number ? (
                        <Link to={`/jobs/${log.jobs.po_number}`} className="text-slate-700 hover:text-slate-900 hover:underline font-medium">
                          {log.jobs.po_number}
                        </Link>
                      ) : '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{log.jobs?.po_line_number || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <Link to={`/part/${log.part_number}`} className="text-slate-700 hover:text-slate-900 hover:underline font-mono">
                        {log.part_number}
                      </Link>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-slate-800 tabular-nums">{log.quantity_produced}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm font-medium ${
                        log.shops?.name === 'NORTH' ? 'bg-blue-100 text-blue-700' :
                        log.shops?.name === 'SOUTH' ? 'bg-green-100 text-green-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {log.shops?.name || '-'}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{log.machines?.name || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleDeleteProductionLog(log.log_id)}
                        className="text-slate-400 hover:text-red-600 transition-colors"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="px-3 py-8 text-center text-slate-400">
                    No production logs found for the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Production;
